// <------------------------ Room Objects ------------------------>

import { cineamoApiClient } from '../../apiClient';
import { buildUrlQuery, UrlQueryParameter } from '../../helpers/url-query-helper';
import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { RoomObjectDto } from '../../models/room-object/RoomObjectDto.types';
import { RoomObjectInputDtoTypes } from '../../models/room-object/RoomObjectInputDto.types';
import { RoomObjectShapeInputDto } from '../../models/room-object/RoomObjectShapeInputDto';
import { ShapeDto } from '../../models/shape/ShapeDto.types';
import { RoomObjectTransformBulkItemInputDto } from '../../models/transform/TransformBulkInputDto';
import { TransformDto } from '../../models/transform/TransformDto.types';
import { TransformInputDto } from '../../models/transform/TransformInputDto';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { RoomObjectListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

import { RoomObjectApiEmbed } from './room-object-api.embed';

type GetRoomObjectsParams = UrlQueryParameter & {
    cinemaRoomId?: CinemaRoomDto['id'];
    embed?: RoomObjectApiEmbed[];
};

export async function getRoomObjects(
    urlParams?: GetRoomObjectsParams
): Promise<ApiResponse<RoomObjectListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<RoomObjectListResponse>>(`/room-objects${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Room Object ------------------------>

export async function getRoomObjectById(id: RoomObjectDto['id']): Promise<ApiResponse<RoomObjectDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<RoomObjectDto>>(`/room-objects/${id}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Create Room Object ------------------------>

export async function createRoomObject(
    roomObject: RoomObjectInputDtoTypes
): Promise<ApiResponse<RoomObjectDto> | ApiErrorResponse<RoomObjectInputDtoTypes>> {
    return cineamoApiClient
        .post('/room-objects', roomObject)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update Room Object ------------------------>

export async function updateRoomObjectById(
    roomObjectId: number,
    roomObject: RoomObjectInputDtoTypes
): Promise<ApiResponse<RoomObjectDto> | ApiErrorResponse<RoomObjectInputDtoTypes>> {
    return cineamoApiClient
        .patch(`/room-objects/${roomObjectId}`, roomObject)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Delete Room Object ------------------------>

export async function deleteRoomObjectById(roomObjectId: number): Promise<boolean> {
    return cineamoApiClient
        .delete(`/room-objects/${roomObjectId}`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

/** Like `deleteRoomObjectById` but reports the HTTP status (`null` for a network failure), so a caller can tell 404 from an outage. */
export async function deleteRoomObjectByIdWithStatus(roomObjectId: number): Promise<number | null> {
    return cineamoApiClient
        .delete(`/room-objects/${roomObjectId}`)
        .then((response) => response.status)
        .catch((error: { response?: { status?: number } }) => error?.response?.status ?? null);
}

// <------------------------ Set Room Object Shape ------------------------>

export async function setRoomObjectShape(
    roomObjectId: number,
    shape: RoomObjectShapeInputDto
): Promise<ApiResponse<ShapeDto> | ApiErrorResponse<RoomObjectShapeInputDto>> {
    return cineamoApiClient
        .put(`/room-objects/${roomObjectId}/shape`, shape)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Set Room Object Transform ------------------------>

export async function setRoomObjectTransform(
    roomObjectId: number,
    transform: TransformInputDto
): Promise<ApiResponse<TransformDto> | ApiErrorResponse<TransformInputDto>> {
    return cineamoApiClient
        .put(`/room-objects/${roomObjectId}/transform`, transform)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Set Many Room Object Transforms ------------------------>

/**
 * Moves up to 500 room objects of one room in a single transaction (all or nothing). Resolves with the HTTP
 * status (`null` for a network failure): 204 means every object was written, 400 names the first item that
 * is not an object of the room.
 */
export async function setRoomObjectTransforms(
    cinemaRoomId: CinemaRoomDto['id'],
    items: RoomObjectTransformBulkItemInputDto[]
): Promise<number | null> {
    return cineamoApiClient
        .patch(`/cinema-rooms/${cinemaRoomId}/room-object-transforms`, items)
        .then((response) => response.status)
        .catch((error: { response?: { status?: number } }) => error?.response?.status ?? null);
}
